/**
 * ReviewPage — magic-link public review page (no auth required).
 *
 * URL: /review/:token
 * Lets a client view drafts the agency sent them, then choose
 *   ✓ Approve   ⟲ Request changes (with comment)   ✕ Reject
 * Each post gets an independent decision. Once all posts have been
 * responded to, the link's rollup status flips on the backend and the
 * agency owner receives an email + in-app notification.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Check, X, RefreshCcw, Calendar, Loader2, AlertCircle, ThumbsUp,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_review: { label: 'Awaiting response', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  changes_requested: { label: 'Changes requested', cls: 'bg-orange-100 text-orange-800' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800' },
};

export const ReviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState({}); // post_id → text
  const [submitting, setSubmitting] = useState({}); // post_id → true

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/reviews/public/${token}`);
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'This link is invalid or expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (postId, decision) => {
    setSubmitting((s) => ({ ...s, [postId]: true }));
    try {
      await axios.post(`${API}/reviews/public/${token}/respond`, {
        post_id: postId,
        decision,
        feedback: feedback[postId] || null,
      });
      toast.success(`Sent: ${decision.replace('_', ' ')}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit response');
    } finally {
      setSubmitting((s) => ({ ...s, [postId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <Card className="max-w-md w-full" data-testid="review-error-card">
          <CardContent className="py-10 text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
            <h2 className="text-lg font-semibold">{error}</h2>
            <p className="text-sm text-zinc-500">If you believe this is a mistake, contact the sender for a fresh link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allDone = data && data.posts.every((p) => p.review_status !== 'pending_review');

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4" data-testid="review-page">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="text-3xl font-bold text-zinc-900">Posts for your review</h1>
          <p className="text-zinc-500 text-sm">
            From <b>{data.agency_name}</b>
            {data.client?.name ? ` · for ${data.client.name}` : ''}
          </p>
          {data.expires_at && (
            <p className="text-xs text-zinc-400">
              Link expires {new Date(data.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Agency message */}
        {data.agency_message && (
          <Card className="bg-indigo-50 border-indigo-200" data-testid="agency-message">
            <CardContent className="py-4 text-sm text-indigo-900">
              <p className="whitespace-pre-wrap">{data.agency_message}</p>
            </CardContent>
          </Card>
        )}

        {/* Posts */}
        {data.posts.map((p) => {
          const status = p.review_status || 'pending_review';
          const isDone = status !== 'pending_review';
          const submittingThis = !!submitting[p.id];
          const heroMedia = (p.media || []).find((m) => m.url);
          return (
            <Card key={p.id} data-testid={`review-post-${p.id}`}>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-lg">{p.post_title || '(Untitled post)'}</CardTitle>
                    <CardDescription className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge variant="outline">{p.platform}</Badge>
                      {p.scheduled_for && (
                        <span className="text-xs flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(p.scheduled_for).toLocaleString()}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <Badge className={STATUS_BADGE[status]?.cls || 'bg-zinc-100'}>
                    {STATUS_BADGE[status]?.label || status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {heroMedia && (
                  <img
                    src={heroMedia.url.startsWith('http') ? heroMedia.url : `${BACKEND_URL}${heroMedia.url}`}
                    alt=""
                    className="w-full max-h-96 object-cover rounded-lg border border-zinc-200"
                  />
                )}
                <div className="prose prose-sm max-w-none">
                  <p className="whitespace-pre-wrap text-zinc-800">{p.caption}</p>
                  {p.hashtags && (
                    <p className="text-indigo-600 text-sm">{p.hashtags}</p>
                  )}
                </div>

                {isDone && p.review_comment && (
                  <div className="bg-zinc-50 p-3 rounded-md text-sm">
                    <span className="font-medium">Your feedback:</span> {p.review_comment}
                  </div>
                )}

                {!isDone && (
                  <div className="space-y-3 pt-2 border-t border-zinc-100">
                    <Textarea
                      placeholder="Optional comment (required for 'Request changes')"
                      value={feedback[p.id] || ''}
                      onChange={(e) => setFeedback({ ...feedback, [p.id]: e.target.value })}
                      data-testid={`feedback-${p.id}`}
                      rows={2}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => respond(p.id, 'approved')}
                        disabled={submittingThis}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`approve-${p.id}`}
                      >
                        {submittingThis ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (!(feedback[p.id] || '').trim()) {
                            toast.error('Please describe what you\'d like changed');
                            return;
                          }
                          respond(p.id, 'changes_requested');
                        }}
                        disabled={submittingThis}
                        data-testid={`changes-${p.id}`}
                      >
                        <RefreshCcw className="w-4 h-4 mr-2" />
                        Request changes
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => respond(p.id, 'rejected')}
                        disabled={submittingThis}
                        className="text-rose-600 border-rose-300 hover:bg-rose-50"
                        data-testid={`reject-${p.id}`}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {allDone && (
          <Card className="bg-emerald-50 border-emerald-200" data-testid="review-complete">
            <CardContent className="py-6 text-center space-y-2">
              <ThumbsUp className="w-8 h-8 text-emerald-600 mx-auto" />
              <p className="font-semibold text-emerald-900">All done — thank you!</p>
              <p className="text-sm text-emerald-700">Your responses have been sent to the agency.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReviewPage;
